/**
 * Presentational timeline renderer for shipment events.
 *
 * Accepts events as props and handles only rendering — no data fetching,
 * no reminders, no chat, no debug menu.  Used by both
 * ShipmentTimelineWithThreadLines (full wrapper) and SimulateRemindersDialog.
 */

import React, { useState, useMemo, useRef, useEffect } from "react";
import {
  Box,
  Typography,
  Collapse,
  Link,
} from "@mui/material";
import FilterListIcon from "@mui/icons-material/FilterList";
import { EventLogger } from "../../utils/eventLogger";

import type { ShipmentTimelineRendererProps } from "./types";
import {
  DEFAULT_LANE_COLORS,
  DEFAULT_OTHER_THREADS_LANE_COLOR,
} from "./constants";
import {
  useThreadCalculations,
  useStickyHeaders,
  useLanePositions,
} from "./hooks";
import {
  EmailDialog,
  TimelineFilters,
  StickyThreadHeader,
  LaneSegments,
  TimelineMessage,
} from "./components";

export function ShipmentTimelineRenderer({
  events,
  numberOfLanes = 4,
  laneThickness = 10,
  laneSpacing = 40,
  lanesLeftOffset = 10,
  bubbleWidth = 50,
  bubbleBaseOffset = 0,
  bubbleReplyOffsetPercent = 0,
  dashedLineLength = 16,
  bottomPadding = 68,
  dateColumnWidth: dateColumnWidthProp = 50,
  laneColors = DEFAULT_LANE_COLORS,
  otherThreadsLaneColor = DEFAULT_OTHER_THREADS_LANE_COLOR,
  filtersCollapsible = false,
  inlineDates = false,
  showEventCategories = false,
  onDownloadAttachment,
  onOpenEmail,
  EmailThreadComponent,
  scrollToEmailId,
  onScrollToEmailHandled,
  showCheckboxes = false,
  selectedEmailIds,
  onToggleEmailSelection,
  onStyleMessage,
}: ShipmentTimelineRendererProps) {
  // When inlineDates is true, collapse the date column so lanes shift left
  const dateColumnWidth = inlineDates ? 0 : dateColumnWidthProp;

  // Filter state
  const [openEmailId, setOpenEmailId] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState<string>("");
  const [showOnlyWithDocuments, setShowOnlyWithDocuments] = useState(false);
  const [selectedThreadIds, setSelectedThreadIds] = useState<string[]>([]);
  const [filtersVisible, setFiltersVisible] = useState(!filtersCollapsible);

  // Highlight state for scrolling to email
  const [highlightedEmailId, setHighlightedEmailId] = useState<string | null>(
    null
  );

  // Refs
  const timelineContainerRef = useRef<HTMLDivElement>(null);
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const messageRefs = useRef<Map<string, HTMLDivElement>>(new Map());

  // Filter and sort events chronologically
  const filteredAndSortedEvents = useMemo(() => {
    let filtered = events;

    const threadMap = new Map<string, { count: number }>();
    events.forEach((event) => {
      if (event.threadId) {
        const existing = threadMap.get(event.threadId);
        if (existing) {
          existing.count++;
        } else {
          threadMap.set(event.threadId, { count: 1 });
        }
      }
    });
    const totalThreads = threadMap.size;

    // Apply thread filter (only skip filtering when ALL threads are selected)
    if (totalThreads > 0 && selectedThreadIds.length < totalThreads) {
      filtered = filtered.filter(
        (event) => event.threadId && selectedThreadIds.includes(event.threadId),
      );
    }

    // Apply document filter
    if (showOnlyWithDocuments) {
      filtered = filtered.filter(
        (event) => event.attachments && event.attachments.length > 0,
      );
    }

    // Apply search query filter
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();

      filtered = filtered.filter((event) => {
        if (event.name?.toLowerCase().includes(query)) return true;
        if (event.emailSubject?.toLowerCase().includes(query)) return true;
        if (event.snippet?.toLowerCase().includes(query)) return true;
        if (event.preview?.toLowerCase().includes(query)) return true;
        if (event.context?.toLowerCase().includes(query)) return true;
        if (
          event.categories
            ?.join(" ")
            .replace(/_/g, " ")
            .toLowerCase()
            .includes(query)
        )
          return true;
        if (
          event.attachments?.some((att: { description?: string }) =>
            att.description?.toLowerCase().includes(query),
          )
        )
          return true;
        return false;
      });
    }

    // Sort by date (oldest first)
    filtered.sort((a, b) => {
      if (a.date && b.date) {
        return new Date(a.date).getTime() - new Date(b.date).getTime();
      }
      return 0;
    });

    return filtered;
  }, [events, searchQuery, showOnlyWithDocuments, selectedThreadIds]);

  // Thread calculations
  const {
    threadInfo,
    laneThreads,
    getLaneInfo,
    threadMessageIndices,
    otherThreadSegments,
  } = useThreadCalculations(
    events,
    filteredAndSortedEvents,
    numberOfLanes,
    laneColors,
  );

  // Lane positions
  const { messageLanePositions, updateLanePositions } = useLanePositions({
    messagesContainerRef,
    messageRefs,
    events: filteredAndSortedEvents,
  });

  // Sticky headers
  const { stickyLaneThreads, stickyOtherThreadSegment } = useStickyHeaders({
    containerRef: timelineContainerRef,
    messageRefs,
    laneThreads,
    threadMessageIndices,
    otherThreadSegments,
    updateLanePositions,
  });

  // Initialize selected threads when events load
  useEffect(() => {
    if (threadInfo.length > 0 && selectedThreadIds.length === 0) {
      setSelectedThreadIds(threadInfo.map((t) => t.threadId));
    }
  }, [threadInfo]); // eslint-disable-line react-hooks/exhaustive-deps

  // Scroll to bottom (most recent messages) when events load
  useEffect(() => {
    if (filteredAndSortedEvents.length > 0 && timelineContainerRef.current) {
      setTimeout(() => {
        if (timelineContainerRef.current) {
          timelineContainerRef.current.scrollTop =
            timelineContainerRef.current.scrollHeight;
        }
      }, 150);
    }
  }, [filteredAndSortedEvents.length]);

  // Handle scroll-to-email from parent (e.g. chat citation)
  useEffect(() => {
    if (!scrollToEmailId) return;

    let msgIndex = filteredAndSortedEvents.findIndex(
      (event) => event.emailId === scrollToEmailId
    );

    if (msgIndex < 0) {
      const existsInAll = events.some(
        (event) => event.emailId === scrollToEmailId
      );

      if (existsInAll) {
        // Clear all filters to show the email
        setSearchQuery("");
        setShowOnlyWithDocuments(false);
        setSelectedThreadIds(threadInfo.map((t) => t.threadId));

        setTimeout(() => {
          const sortedEvents = [...events].sort((a, b) => {
            if (a.date && b.date) {
              return new Date(a.date).getTime() - new Date(b.date).getTime();
            }
            return 0;
          });
          const newMsgIndex = sortedEvents.findIndex(
            (event) => event.emailId === scrollToEmailId
          );
          if (newMsgIndex >= 0) {
            scrollToMessageAndHighlight(scrollToEmailId, newMsgIndex);
          }
          onScrollToEmailHandled?.();
        }, 100);
        return;
      }

      console.log("Email not found in timeline:", scrollToEmailId);
      onScrollToEmailHandled?.();
      return;
    }

    scrollToMessageAndHighlight(scrollToEmailId, msgIndex);
    onScrollToEmailHandled?.();
  }, [scrollToEmailId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Event handlers
  const handleOpenEmail = (eventEmailId: string) => {
    EventLogger.getInstance().track({
      name: "click",
      action: "open_mail",
    });
    if (onOpenEmail) {
      onOpenEmail(eventEmailId);
    } else {
      setOpenEmailId(eventEmailId);
    }
  };

  const handleCloseEmail = () => {
    setOpenEmailId(null);
  };

  const handleDownloadAttachment = (
    emailId: string,
    attachmentId: string,
    filename?: string,
  ) => {
    onDownloadAttachment?.(emailId, attachmentId, filename);
  };

  // Helper to scroll to a message and highlight it
  const scrollToMessageAndHighlight = (emailId: string, msgIndex: number) => {
    const msgElement = messageRefs.current.get(`msg-${msgIndex}`);
    if (msgElement && timelineContainerRef.current) {
      const container = timelineContainerRef.current;
      const msgRect = msgElement.getBoundingClientRect();
      const containerRect = container.getBoundingClientRect();
      const targetScroll =
        msgRect.top - containerRect.top + container.scrollTop - 100;

      const startScroll = container.scrollTop;
      const distance = targetScroll - startScroll;
      const duration = 250;
      const startTime = performance.now();

      const animateScroll = (currentTime: number) => {
        const elapsed = currentTime - startTime;
        const progress = Math.min(elapsed / duration, 1);
        const easeOut = 1 - Math.pow(1 - progress, 3);
        container.scrollTop = startScroll + distance * easeOut;

        if (progress < 1) {
          requestAnimationFrame(animateScroll);
        }
      };

      requestAnimationFrame(animateScroll);
    }

    setHighlightedEmailId(emailId);
    setTimeout(() => setHighlightedEmailId(null), 4000);

    EventLogger.getInstance().track({
      name: "click",
      action: "open_mail",
    });
  };

  // Gray lane X position
  const grayLaneX =
    lanesLeftOffset + laneThreads.length * (laneThickness + laneSpacing);

  if (events.length === 0) {
    return (
      <Box sx={{ p: 2 }}>
        <Typography variant="body2" color="text.secondary">
          No events found for this shipment
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      className="ShipmentTimelineRenderer"
      sx={{
        flex: 1,
        minHeight: 0,
        height: "100%",
        overflow: "hidden",
        display: "flex",
        flexDirection: "column",
        position: "relative",
      }}
    >
      {/* Filters Toggle (only when collapsible) */}
      {filtersCollapsible && (
        <Box
          sx={{ pr: 2, pt: 0.5, display: "flex", justifyContent: "flex-end" }}
        >
          <Box
            onClick={() => setFiltersVisible((v) => !v)}
            sx={{
              display: "inline-flex",
              alignItems: "center",
              gap: 0.5,
              cursor: "pointer",
              color: filtersVisible ? "primary.main" : "text.secondary",
              "&:hover": { color: "primary.main" },
              transition: "color 0.15s",
            }}
          >
            <FilterListIcon sx={{ fontSize: "1rem" }} />
            <Typography variant="caption" sx={{ fontWeight: 500 }}>
              Filters
            </Typography>
          </Box>
        </Box>
      )}

      {/* Filters Row */}
      <Collapse in={filtersVisible}>
        <Box sx={{ pt: 1, pr: 2, flexShrink: 0 }}>
          <TimelineFilters
            searchQuery={searchQuery}
            onSearchChange={setSearchQuery}
            showOnlyWithDocuments={showOnlyWithDocuments}
            onShowOnlyWithDocumentsChange={setShowOnlyWithDocuments}
            threadInfo={threadInfo}
            selectedThreadIds={selectedThreadIds}
            onThreadFilterChange={setSelectedThreadIds}
          />
        </Box>
      </Collapse>

      {/* No filtered results */}
      {searchQuery.trim() && filteredAndSortedEvents.length === 0 && (
        <Box sx={{ p: 2 }}>
          <Typography variant="body2" color="text.secondary">
            No messages match your search
          </Typography>
        </Box>
      )}

      {/* Timeline with Thread Lines */}
      {(!searchQuery.trim() || filteredAndSortedEvents.length > 0) && (
        <Box
          className="timeline-container"
          ref={timelineContainerRef}
          sx={{
            position: "relative",
            flex: 1,
            minHeight: 0,
            overflow: "auto",
          }}
        >
          {/* Sticky Header container */}
          <Box
            sx={{
              position: "sticky",
              top: 0,
              zIndex: 10,
              minHeight: 0,
            }}
          >
            <StickyThreadHeader
              stickyLaneThreads={stickyLaneThreads}
              stickyOtherThreadSegment={stickyOtherThreadSegment}
              laneThreads={laneThreads}
              otherThreadSegments={otherThreadSegments}
              laneThickness={laneThickness}
              laneSpacing={laneSpacing}
              lanesLeftOffset={lanesLeftOffset}
              otherThreadsLaneColor={otherThreadsLaneColor}
              dateColumnWidth={dateColumnWidth}
            />
          </Box>

          {/* Content area with lanes and messages */}
          <Box
            className="messages-and-lanes-container"
            sx={{
              position: "relative",
              zIndex: 0,
              minHeight: 200,
              pb: `${bottomPadding}px`,
            }}
          >
            {/* Lane segments */}
            <Box
              sx={{
                position: "absolute",
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                zIndex: 2,
                pointerEvents: "none",
              }}
            >
              <LaneSegments
                events={filteredAndSortedEvents}
                messageLanePositions={messageLanePositions}
                getLaneInfo={getLaneInfo}
                threadMessageIndices={threadMessageIndices}
                stickyLaneThreads={stickyLaneThreads}
                laneThickness={laneThickness}
                laneSpacing={laneSpacing}
                lanesLeftOffset={lanesLeftOffset}
                dashedLineLength={dashedLineLength}
                otherThreadsLaneColor={otherThreadsLaneColor}
                laneThreadsCount={laneThreads.length}
                dateColumnWidth={dateColumnWidth}
              />
            </Box>

            {/* Messages */}
            <Box
              ref={messagesContainerRef}
              sx={{
                position: "relative",
              }}
            >
              {filteredAndSortedEvents.map((event, index) => {
                const prevEvent =
                  index > 0 ? filteredAndSortedEvents[index - 1] : null;

                const showSubject =
                  !prevEvent || prevEvent.threadId !== event.threadId;

                const currentSender = event.emailFrom?.[0] || "";
                const prevSender = prevEvent?.emailFrom?.[0] || "";
                const showSender =
                  currentSender !== prevSender || showSubject;

                const isSameSenderAsPrevious =
                  currentSender === prevSender && !showSubject;

                const currentDate = event.date
                  ? new Date(event.date).toDateString()
                  : "";
                const prevDate = prevEvent?.date
                  ? new Date(prevEvent.date).toDateString()
                  : "";
                const showDateBullet = currentDate !== prevDate;

                const laneInfo = getLaneInfo(event.threadId);

                const threadIndices = event.threadId
                  ? threadMessageIndices.get(event.threadId)
                  : undefined;
                const isFirstMessageOfLaneThread =
                  threadIndices?.first === index;
                const isThreadSticky = event.threadId
                  ? stickyLaneThreads.has(event.threadId)
                  : false;
                const showLaneSubjectInline = !!(
                  laneInfo && isFirstMessageOfLaneThread
                );
                const isLaneSubjectHidden = isThreadSticky;

                const isFirstOfThreadChunk = showSubject && !!prevEvent;
                let lane: { color: string; xPosition: number };
                let showSubjectInline: boolean;
                let isSubjectHidden: boolean;

                if (laneInfo) {
                  lane = {
                    color: laneInfo.color,
                    xPosition:
                      lanesLeftOffset +
                      laneInfo.laneIndex * (laneThickness + laneSpacing),
                  };
                  showSubjectInline = showLaneSubjectInline;
                  isSubjectHidden = isLaneSubjectHidden;
                } else {
                  const segmentIndex = otherThreadSegments.findIndex(
                    (seg) =>
                      seg.firstIndex <= index && index <= seg.lastIndex,
                  );
                  const segment =
                    segmentIndex >= 0
                      ? otherThreadSegments[segmentIndex]
                      : null;
                  const isFirstOfSegment = segment
                    ? segment.firstIndex === index
                    : false;
                  const isSegmentSticky =
                    stickyOtherThreadSegment === segmentIndex;

                  lane = {
                    color: otherThreadsLaneColor,
                    xPosition: grayLaneX,
                  };
                  showSubjectInline = isFirstOfSegment;
                  isSubjectHidden = isSegmentSticky;
                }

                return (
                  <TimelineMessage
                    key={event.emailId ? `${event.emailId}-${index}` : `msg-${index}`}
                    event={event}
                    lane={lane}
                    showSubjectInline={showSubjectInline}
                    isSubjectHidden={isSubjectHidden}
                    showSender={showSender}
                    showDateBullet={showDateBullet}
                    isSameSenderAsPrevious={isSameSenderAsPrevious}
                    isFirstOfThreadChunk={isFirstOfThreadChunk}
                    onOpenEmail={handleOpenEmail}
                    onDownloadAttachment={handleDownloadAttachment}
                    laneThickness={laneThickness}
                    bubbleWidth={bubbleWidth}
                    bubbleBaseOffset={bubbleBaseOffset}
                    bubbleReplyOffsetPercent={bubbleReplyOffsetPercent}
                    dateColumnWidth={dateColumnWidth}
                    inlineDates={inlineDates}
                    showEventCategories={showEventCategories}
                    isHighlighted={highlightedEmailId === event.emailId}
                    showCheckbox={showCheckboxes}
                    isSelected={event.emailId ? selectedEmailIds?.has(event.emailId) ?? true : true}
                    onToggleSelection={event.emailId && onToggleEmailSelection ? () => onToggleEmailSelection(event.emailId!) : undefined}
                    customStyle={event.emailId && onStyleMessage ? onStyleMessage(event.emailId) : undefined}
                    refCallback={(el) => {
                      if (el) {
                        messageRefs.current.set(`msg-${index}`, el);
                      }
                    }}
                  />
                );
              })}
            </Box>

            {/* End of Timeline Footer */}
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                pt: 4,
                pb: 10,
              }}
            >
              <Typography
                variant="body2"
                sx={{
                  color: "text.secondary",
                  fontSize: "0.75rem",
                  textAlign: "center",
                }}
              >
                End of email exchanges, total {filteredAndSortedEvents.length}{" "}
                emails.
                <br />
                Scroll up for earlier messages, or{" "}
                <Link
                  component="span"
                  onClick={() => {
                    if (timelineContainerRef.current) {
                      timelineContainerRef.current.scrollTo({
                        top: 0,
                        behavior: "smooth",
                      });
                    }
                  }}
                  sx={{
                    cursor: "pointer",
                    color: "primary.main",
                    textDecoration: "underline",
                  }}
                >
                  jump to top ↑
                </Link>
              </Typography>
            </Box>
          </Box>
        </Box>
      )}

      {/* Email Dialog — only rendered when onOpenEmail is NOT provided */}
      {!onOpenEmail && (
        <EmailDialog
          emailId={openEmailId}
          onClose={handleCloseEmail}
          EmailThreadComponent={EmailThreadComponent}
        />
      )}
    </Box>
  );
}
